import { FC, useMemo } from 'react'
import cn from 'classnames'
import { ArrowLeftOutlined, LockOutlined } from '@ant-design/icons'
import type { IntegrationChannelItem } from '@/api/integrations/integrations.types'
import { CompressIcon } from '@/utils/icons'
import WazzupIcon from '@pages/Admin/CRM/components/PhoneIntegrationButtons/WazzupIcon'
import VkUnreadCountBadge from '@pages/Admin/CRM/components/integrations/vk/components/VkUnreadCountBadge'
import { formatCrmChatUnreadBadge } from '@pages/Admin/CRM/utils/leadFields'
import { getWazzupChannelTypeLabel } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/WazzupCrmSettings/lib/wazzupCrmIntegration.helpers'
import { useWazzupCrmChannels } from './useWazzupCrmChannels'
import { useWazzupChannelUnreadCounts } from './useWazzupChannelUnreadCounts'
import classes from './IntegrationChatDock.module.css'

type IntegrationChatDockWazzupChannelsPickerProps = {
  integrationId: number
  accountName?: string | null
  accountAccessible?: boolean
  onCollapse?: () => void
  onBack: () => void
  onSelectChannel: (channel: IntegrationChannelItem) => void
}

const formatPickerUnreadHint = (count: number): string | null => {
  if (!Number.isFinite(count) || count <= 0) {
    return null
  }
  const normalized = Math.floor(count)
  if (normalized === 1) {
    return '1 непрочитанное'
  }
  return `${normalized > 99 ? '99+' : normalized} непрочитанных`
}

const getChannelTitle = (channel: IntegrationChannelItem): string => {
  const name = channel.name?.trim()
  if (name) return name
  const typeLabel = getWazzupChannelTypeLabel(String(channel.type ?? ''))
  if (typeLabel && typeLabel !== 'неизвестный') {
    return typeLabel
  }
  return `Канал #${channel.id}`
}

/** Подпись type с бэка (whatsapp / maxbot / vk / …). */
const getChannelTypeHint = (channel: IntegrationChannelItem): string | null => {
  const raw = String(channel.type ?? '').trim()
  if (!raw) {
    return null
  }
  return getWazzupChannelTypeLabel(raw)
}

/** По доке: blocked/unauthorized — badge, но в inbox пускаем (диалоги могут быть). */
const isWazzupChannelSelectable = (channel: IntegrationChannelItem): boolean => {
  const status = String(channel.status ?? '').toLowerCase()
  if (status === 'inactive') {
    return false
  }
  const externalId = String(channel.external_channel_id ?? '').trim()
  return externalId.length > 0
}

const getWazzupChannelStatusHint = (
  channel: IntegrationChannelItem,
): string | null => {
  const status = String(channel.status ?? '').toLowerCase()
  if (status === 'inactive') return 'Неактивен'
  if (status === 'blocked') return 'Заблокирован'
  if (status === 'unauthorized') return 'Нет авторизации'
  if (status && status !== 'active') return status
  return null
}

const IntegrationChatDockWazzupChannelsPicker: FC<
  IntegrationChatDockWazzupChannelsPickerProps
> = ({
  integrationId,
  accountName,
  accountAccessible = true,
  onCollapse,
  onBack,
  onSelectChannel,
}) => {
  const { channels, isLoading, error, retry } = useWazzupCrmChannels(
    integrationId,
    true,
  )
  const { unreadByExternalChannelId } = useWazzupChannelUnreadCounts(
    integrationId,
    true,
  )

  const subtitle = useMemo(() => {
    const account = accountName?.trim()
    if (account) {
      return `Каналы аккаунта «${account}»`
    }
    return 'Выберите канал'
  }, [accountName])

  return (
    <div className={classes.pickerRoot}>
      <header className={classes.pickerHeader}>
        <button
          type="button"
          className={classes.pickerBackButton}
          aria-label="Назад к аккаунтам Wazzup"
          onClick={onBack}
        >
          <ArrowLeftOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        </button>
        <div className={classes.pickerHeaderMain}>
          <span className={classes.pickerTitle}>Каналы Wazzup</span>
          <span className={classes.pickerSubtitle}>{subtitle}</span>
        </div>
        {onCollapse ? (
          <button
            type="button"
            className={classes.pickerCollapseButton}
            aria-label="Свернуть чат"
            onClick={onCollapse}
          >
            <CompressIcon />
          </button>
        ) : null}
      </header>

      {!accountAccessible ? (
        <div className={classes.pickerBannerHint}>
          <LockOutlined
            className={classes.pickerCardLock}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
          Нет доступа к аккаунту — каналы могут быть недоступны
        </div>
      ) : null}

      {isLoading ? (
        <div className={classes.pickerStatus}>Загрузка каналов…</div>
      ) : null}

      {!isLoading && error ? (
        <div className={classes.pickerStatus}>
          <span>{error}</span>
          <button
            type="button"
            className={classes.pickerRetryButton}
            onClick={retry}
          >
            Повторить
          </button>
        </div>
      ) : null}

      {!isLoading && !error && channels.length === 0 ? (
        <div className={classes.pickerStatus}>Каналов пока нет</div>
      ) : null}

      {!isLoading && !error && channels.length > 0 ? (
        <div className={classes.pickerGrid}>
          {channels.map((channel) => {
            const title = getChannelTitle(channel)
            const typeHint = getChannelTypeHint(channel)
            const isSelectable = isWazzupChannelSelectable(channel)
            const statusHint = getWazzupChannelStatusHint(channel)
            const externalChannelId = String(
              channel.external_channel_id ?? '',
            ).trim()
            const unreadCount =
              unreadByExternalChannelId[externalChannelId] ?? 0
            const unreadLabel =
              isSelectable && unreadCount > 0
                ? formatCrmChatUnreadBadge(unreadCount)
                : ''
            const unreadHint = isSelectable
              ? formatPickerUnreadHint(unreadCount)
              : null
            const ariaParts = [title, typeHint, statusHint, unreadHint].filter(
              Boolean,
            )

            return (
              <button
                key={String(channel.id)}
                type="button"
                className={cn(classes.pickerCard, {
                  [classes.pickerCard_hasUnread]: Boolean(unreadLabel),
                })}
                disabled={!isSelectable}
                aria-disabled={!isSelectable}
                aria-label={ariaParts.join(', ')}
                onClick={() => {
                  if (!isSelectable) return
                  onSelectChannel(channel)
                }}
              >
                {unreadLabel ? (
                  <VkUnreadCountBadge
                    label={unreadLabel}
                    placement="picker"
                    dock
                    aria-label={`Непрочитанных: ${unreadCount}`}
                  />
                ) : null}
                <span
                  className={classes.pickerCardIconWrap}
                  style={{
                    color: isSelectable
                      ? 'var(--color-status-success-text)'
                      : undefined,
                  }}
                >
                  <WazzupIcon className={classes.pickerCardIcon} />
                </span>
                <span className={classes.pickerCardLabel}>{title}</span>
                {typeHint ? (
                  <span className={classes.pickerCardTypeBadge}>{typeHint}</span>
                ) : null}
                {unreadHint ? (
                  <span className={classes.pickerCardUnreadHint}>
                    {unreadHint}
                  </span>
                ) : null}
                {statusHint ? (
                  <span className={classes.pickerCardHint}>{statusHint}</span>
                ) : null}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

export default IntegrationChatDockWazzupChannelsPicker
